import AppointmentType from "./AppointmentType";
import AppointmentStatus from "./AppointmentStatus";
import {PollItem} from "./PollItem";
import MemberPollItem from "./MemberPollItem";
import Member from "./Member";
import {DatePollInfo, MemberAndAttendType} from "./DatePollInfo";
import {getShortenDateWithDayOfWeek, getUniqueDates} from "../utils/Utils";
import TimeType from "./TimeType";
import dayjs from "dayjs";

export class Appointment {
    id: string;
    private _name: string | null;
    type: AppointmentType;
    status: AppointmentStatus;
    comment: string | null;
    pollItems: PollItem[];
    memberPollItems: MemberPollItem[];
    fixedPollItem: PollItem | null;
    members: Member[];
    joinKey: string | null;
    createdAt: Date;
    lastModifiedAt: Date;

    constructor(id: string, name: string | null, type: AppointmentType, status: AppointmentStatus, comment: string | null, pollItems: PollItem[], memberPollItems: MemberPollItem[], fixedPollItem: PollItem | null, members: Member[], joinKey: string | null, createdAt: Date, lastModifiedAt: Date) {
        this.id = id;
        this._name = name;
        this.type = type;
        this.status = status;
        this.comment = comment;
        this.pollItems = pollItems;
        this.memberPollItems = memberPollItems;
        this.fixedPollItem = fixedPollItem;
        this.members = members;
        this.joinKey = joinKey;
        this.createdAt = createdAt;
        this.lastModifiedAt = lastModifiedAt;
    }

    get name(): string {
        return this._name ?? this.getDefaultName();
    }

    static fromJson(json: any): Appointment {
        return new Appointment(
            json.id,
            json.name,
            json.type as AppointmentType,
            json.status as AppointmentStatus,
            json.comment,
            json.pollItems.map((item: any) => PollItem.fromJson(item)),
            json.memberPollItems.map((item: any) => MemberPollItem.fromJson(item)),
            json.fixedPollItem ? PollItem.fromJson(json.fixedPollItem) : null,
            json.members.map((member: any) => Member.fromJson(member)),
            json.joinKey,
            dayjs(json.createdAt).toDate(),
            dayjs(json.lastModifiedAt).toDate(),
        )
    }

    toDatePollInfos(): DatePollInfo[] {
        const dates = this.pollItems.map((pollItem) => pollItem.date);
        const uniqueDates = getUniqueDates(dates);
        const datePollInfos = uniqueDates.map((date) => new DatePollInfo(date, []))
        for (const memberPollItem of this.memberPollItems) {
            const pollItem = this.pollItems.find((pollItem) => pollItem.id === memberPollItem.pollItem.id)
            if (!pollItem) {
                continue;
            }

            const datePollInfo = datePollInfos.find((info) => info.date.getTime() === pollItem.date.getTime())
            if (!datePollInfo) {
                continue;
            }

            const member = this.members.find((member) => member.id === memberPollItem.member.id)
            if (!member) {
                continue;
            }

            datePollInfo.pollData.push(new MemberAndAttendType(member, memberPollItem.attendType))
        }

        return datePollInfos;
    }

    getTop3PollInfos(): DatePollInfo[] {
        const datePollInfos = this.toDatePollInfos();
        const sortedDatePollInfos = [...datePollInfos].sort((a, b) => b.getScore() - a.getScore());
        return sortedDatePollInfos.slice(0, 3);
    }

    /**
     * Top3 중 베스트 날짜 고름.
     * 즉, 타이 기록이 3개 초과여도 3개까지만 하트 표시
     */
    getBestPollInfos(): DatePollInfo[] {
        const datePollInfos = this.getTop3PollInfos();
        let maxScore = 0;
        datePollInfos.forEach(datePollInfo => {
            if (datePollInfo.getScore() > maxScore) {
                maxScore = datePollInfo.getScore();
            }
        })

        return datePollInfos.filter(datePollInfo => datePollInfo.getScore() === maxScore);
    }

    /**
     * 약속에 참여한 멤버들 중 투표한 멤버를 중복 없이 반환함
     */
    getVotedMembers(): Member[] {
        const members: Member[] = [];

        for (const item of this.memberPollItems) {
            if (!members.find(member => member.id === item.member.id)) {
                members.push(item.member);
            }
        }

        return members;
    }

    /**
     * UserAppointment와 중복. 둘 중 하나를 없애자.
     */
    getPollDateFirstDate(): Date | null {
        const uniqueDates = this.getSortedPollItemDates();
        return uniqueDates[0];
    }

    /**
     * UserAppointment와 중복. 둘 중 하나를 없애자.
     */
    getPollDateLastDate(): Date | null {
        const uniqueDates = this.getSortedPollItemDates();
        return uniqueDates[uniqueDates.length - 1];
    }

    getActiveDates(): Date[] {
        return this.pollItems.map((pollItem) => pollItem.date);
    }

    private getDefaultName(): string {
        return `${getShortenDateWithDayOfWeek(this.createdAt)}에 생성된 ${TimeType.getTimeTypeString(this.getAppointmentTimeType())} 투표`
    }

    /**
     * 약속 시간대를 반환하는 함수.
     * 약속 하위 후보(PollItem) 중 약속 시간대가 다른 항목이 있는 경우 예외를 던짐.
     *
     * 240901 현재 모든 약속 하위 후보(PollItem)들은 같은 시간 타입을 가진다.
     * 해당 정책을 기반으로 만들어진 함수이기 때문에, 추후 해당 정책이 변경되는 경우 정상동작하지 않게 된다.
     */
    private getAppointmentTimeType(): TimeType {
        const appointmentTimeType = this.pollItems[0].timeType;
        const containsMultipleTimeTypes = this.pollItems.some((pollItem) => appointmentTimeType !== pollItem.timeType)
        if (containsMultipleTimeTypes) {
            throw new Error("약속 하위 후보들의 시간대가 서로 다릅니다.");
        }

        return appointmentTimeType;
    }

    /**
     * UserAppointment와 중복. 둘 중 하나를 없애자.
     */
    private getSortedPollItemDates(): Date[] {
        if (this.pollItems.length === 0) {
            return [];
        }

        if (this.pollItems.length === 1) {
            return [this.pollItems[0].date];
        }

        const dates = this.pollItems.map((pollItem) => pollItem.date);
        const uniqueDates = getUniqueDates(dates);

        return uniqueDates.sort((a, b) => a.getTime() - b.getTime())
    }
}